<template>
  <Teleport to="body">
    <div v-if="visible" class="get-power-dialog">
      <div class="dialog-overlay" @click="close"></div>
      <div class="dialog-content">
        <div class="container">
          <div class="header">
            <h1><i class="fas fa-bolt"></i> {{ $t('manga.getPower.title') }}</h1>
            <p class="subtitle">{{ $t('manga.getPower.subtitle') }}<a href="https://space.bilibili.com/4182741" target="_blank" class="contact-btn"><i class="fas fa-external-link-alt"></i> {{ $t('manga.getPower.contactAdmin') }}</a></p>

            <div class="current-balance">
              <div class="balance-icon">
                <i class="fas fa-coins"></i>
              </div>
              <div>
                <div class="balance-label">{{ $t('manga.getPower.currentBalance') }}</div>
                <div class="balance-amount">{{ currentPower }} <span style="font-size: 0.9rem; color: #94a3b8;">{{ $t('manga.getPower.creditsUnit') }}</span></div>
              </div>
            </div>
          </div>

          <div class="section">
            <h2 class="section-title"><i class="fas fa-shopping-cart"></i> {{ $t('manga.getPower.packageTitle') }}</h2>
            <p style="margin-bottom: 16px; color: #94a3b8; font-size: 0.9rem;">{{ $t('manga.getPower.packageDesc') }}</p>

            <div class="packages" style="position: relative;">
              <div v-for="item in commodityList" :key="item.id" class="package-card"
                :class="{ active: selectedCommodity && selectedCommodity.id === item.id }" @click="selectCommodity(item)">
                <div v-if="item.tag" class="popular-badge">{{ item.tag }}</div>
                <div class="package-amount">{{ item.name }}</div>
                <div class="package-price">￥{{ item.price }}</div>
                <div v-if="item.description" class="package-desc">{{ item.description }}</div>
              </div>
              <div v-if="commodityList.length === 0 && !loadingCommodity" class="package-empty">{{ $t('manga.getPower.noPackage') }}</div>
            </div>

            <div class="payment-methods">
              <div class="payment-label">{{ $t('manga.getPower.payMethod') }}</div>
              <div class="payment-options">
                <div class="payment-option" :class="{ active: selectedPayType === 2 }" @click="selectedPayType = 2">
                  <input type="radio" name="payment" :checked="selectedPayType === 2" />
                  <img :src="zfbLogo" :alt="$t('manga.getPower.alipay')" class="payment-logo" />
                  <span>{{ $t('manga.getPower.alipay') }}</span>
                </div>
                <div class="payment-option" :class="{ active: selectedPayType === 3 }" @click="selectedPayType = 3">
                  <input type="radio" name="payment" :checked="selectedPayType === 3" />
                  <img :src="wxLogo" :alt="$t('manga.getPower.wechatPay')" class="payment-logo" />
                  <span>{{ $t('manga.getPower.wechatPay') }}</span>
                </div>
                <!-- PayPal 付款方式 (暂时隐藏，后续启用) -->
                <!--
                <div class="payment-option" :class="{ active: selectedPayType === 5 }" @click="selectedPayType = 5">
                  <input type="radio" name="payment" :checked="selectedPayType === 5" />
                  <img :src="paypalLogo" alt="PayPal" class="payment-logo" />
                  <span>PayPal</span>
                </div>
                -->
              </div>
            </div>

            <button class="buy-button" :disabled="!selectedCommodity || paying" @click="handlePurchase">
              <span v-if="paying" class="btn-loading"><i class="fas fa-spinner fa-spin"></i></span>
              {{ paying ? $t('manga.getPower.payProcessing') : getPurchaseButtonText() }}
            </button>

            <div class="privacy-notice">
              <i class="fas fa-shield-alt"></i>
              <div>
                <strong>{{ $t('manga.getPower.privacyTitle') }}</strong>
                <p>{{ $t('manga.getPower.privacyDesc') }}</p>
              </div>
            </div>
          </div>

          <div class="section">
            <h2 class="section-title"><i class="fas fa-road"></i> {{ $t('manga.getPower.channelCompare') }}</h2>
            <p style="margin-bottom: 20px; color: rgba(255, 255, 255, 0.8);">{{ $t('manga.getPower.channelDesc') }}</p>

            <div class="comparison">
              <div class="queue-type free">
                <h3 class="queue-title free"><i class="fas fa-users"></i> {{ $t('manga.login.freeQueue') }}</h3>
                <p style="color: rgba(255, 255, 255, 0.8); margin-bottom: 15px;">{{ $t('manga.login.freeQueueDesc') }}</p>
                <ul class="feature-list">
                  <li v-for="(item, i) in $tm('manga.login.freeItems')" :key="i"><i :class="i < 2 ? 'fas fa-check' : 'fas fa-times'"></i> {{ item }}</li>
                </ul>
              </div>

              <div class="queue-type vip">
                <h3 class="queue-title vip"><i class="fas fa-crown"></i> {{ $t('manga.login.vipQueue') }}</h3>
                <p style="color: rgba(255, 255, 255, 0.8); margin-bottom: 15px;">{{ $t('manga.login.vipQueueDesc') }}</p>
                <ul class="feature-list">
                  <li v-for="(item, i) in $tm('manga.login.vipItems')" :key="i"><i class="fas fa-check"></i> {{ item }}</li>
                </ul>
              </div>
            </div>

            <div style="margin-top: 20px; padding: 15px; background-color: rgba(94, 234, 212, 0.1); border-radius: 8px; border: 1px solid rgba(94, 234, 212, 0.2);">
              <h4 style="color: #5eead4; margin-bottom: 10px;"><i class="fas fa-lightbulb"></i> {{ $t('manga.getPower.usageTipTitle') }}</h4>
              <p style="color: rgba(255, 255, 255, 0.9);">{{ $t('manga.getPower.usageTipDesc') }}</p>
            </div>

            <div style="margin-top: 16px; padding: 15px; background-color: rgba(167, 139, 250, 0.08); border-radius: 8px; border: 1px solid rgba(167, 139, 250, 0.2);">
              <h4 style="color: #a78bfa; margin-bottom: 10px;"><i class="fas fa-calculator"></i> {{ $t('manga.getPower.calcTitle') }}</h4>
              <p style="color: rgba(255, 255, 255, 0.9);">{{ $t('manga.getPower.calcDesc') }}</p>
            </div>
          </div>

          <div class="footer-note">
            <p><i class="fas fa-exclamation-circle"></i> {{ $t('manga.getPower.calcDesc') }}</p>
          </div>
        </div>

        <button class="close-button" @click="close">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>
  </Teleport>

  <!-- 微信支付二维码弹窗 -->
  <Teleport to="body">
    <div v-if="showWxQrcode" class="qrcode-dialog">
      <div class="qrcode-overlay" @click="closeWxQrcode"></div>
      <div class="qrcode-content">
        <div class="qrcode-header">
          <img :src="wxLogo" :alt="$t('manga.getPower.wechatPay')" class="qrcode-logo" />
          <h3>{{ $t('manga.getPower.wechatPay') }}</h3>
        </div>
        <div class="qrcode-body">
          <img :src="qrcodeUrl" :alt="$t('manga.getPower.wechatPay')" class="qrcode-image" />
          <p class="qrcode-tip">{{ $t('manga.getPower.qrcodeDesc', { method: $t('manga.getPower.wechatPay') }) }}</p>
          <p class="qrcode-amount">{{ $t('manga.getPower.creditsUnit') }}：<span>￥{{ payAmount }}</span></p>
        </div>
        <div class="qrcode-footer">
          <button class="qrcode-close-btn" @click="closeWxQrcode">{{ $t('translate.cancel') }}</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch } from 'vue'
import { createPayOrder, getCommodityList, queryWxTradeStatus, capturePayPalOrder } from '@/api/manga/manga.js'
import { ElMessage } from 'element-plus'
import { useI18n } from 'vue-i18n'
import zfbLogo from '@/assets/ZFB_logo.png'
import wxLogo from '@/assets/WX_logo.png'
import paypalLogo from '@/assets/PayPal_logo.svg'

const { t } = useI18n()

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  currentPower: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['update:visible', 'purchase'])

// 商品列表
const commodityList = ref([])
const loadingCommodity = ref(false)
const selectedCommodity = ref(null)

// 支付方式：2-支付宝，3-微信
const selectedPayType = ref(2)

// 支付中状态
const paying = ref(false)

// 微信支付二维码
const showWxQrcode = ref(false)
const qrcodeUrl = ref('')
const payAmount = ref('')
const currentOutTradeNo = ref('')
let pollTimer = null

// 获取算力商品列表
const fetchCommodityList = async () => {
  loadingCommodity.value = true
  try {
    const res = await getCommodityList()
    if (res.data && res.data.records) {
      commodityList.value = res.data.records
    } else if (Array.isArray(res.data)) {
      commodityList.value = res.data
    }
    // 默认选中第一个
    if (commodityList.value.length > 0 && !selectedCommodity.value) {
      selectedCommodity.value = commodityList.value[0]
    }
  } catch (e) {
    console.error('获取商品列表失败:', e)
  } finally {
    loadingCommodity.value = false
  }
}

// 弹窗打开时加载商品列表
watch(() => props.visible, (val) => {
  if (val) {
    fetchCommodityList()
  }
})

// 选择套餐
const selectCommodity = (item) => {
  selectedCommodity.value = item
}

// 获取购买按钮文本
const getPurchaseButtonText = () => {
  if (!selectedCommodity.value) return t('manga.getPower.selectPackage')
  return `${t('manga.getPower.buyButton')} ${selectedCommodity.value.name} (￥${selectedCommodity.value.price})`
}

// 处理购买
const handlePurchase = async () => {
  if (!selectedCommodity.value) {
    ElMessage.warning(t('manga.getPower.pleaseSelectPackage'))
    return
  }
  paying.value = true
  try {
    const res = await createPayOrder({
      commodityId: selectedCommodity.value.id,
      payType: selectedPayType.value
    })
    if (res.code === 200 && res.data) {
      const { outTradeNo, payInfo } = res.data
      if (selectedPayType.value === 2) {
        // 支付宝：打开新窗口，写入form表单并自动提交
        const payWindow = window.open('', '_blank')
        payWindow.document.write(payInfo)
        payWindow.document.close()
      } else if (selectedPayType.value === 3) {
        // 微信：显示二维码
        payAmount.value = selectedCommodity.value.price
        currentOutTradeNo.value = outTradeNo
        qrcodeUrl.value = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(payInfo)}`
        showWxQrcode.value = true
        // 开始轮询支付状态
        startPollPaymentStatus()
      } else if (selectedPayType.value === 5) {
        // PayPal：存储订单信息，跳转到PayPal支付页面
        localStorage.setItem('paypal_outTradeNo', outTradeNo)
        window.location.href = payInfo
      }
    } else {
      ElMessage.error(res.message || t('manga.getPower.createOrderFailed'))
    }
  } catch (e) {
    ElMessage.error(t('manga.getPower.createOrderFailed'))
  } finally {
    paying.value = false
  }
}

// 开始轮询微信支付状态
const startPollPaymentStatus = () => {
  // 清除之前的轮询
  if (pollTimer) {
    clearInterval(pollTimer)
  }
  pollTimer = setInterval(async () => {
    if (!showWxQrcode.value) {
      clearInterval(pollTimer)
      return
    }
    try {
      const res = await queryWxTradeStatus(currentOutTradeNo.value)
      if (res.code === 200 && res.data === 'SUCCESS') {
        clearInterval(pollTimer)
        showWxQrcode.value = false
        ElMessage.success(t('manga.getPower.paySuccess'))
        emit('purchase')
        close()
      }
    } catch (e) {
      console.error('查询支付状态失败:', e)
    }
  }, 3000) // 每3秒查询一次
}

// 关闭微信二维码弹窗
const closeWxQrcode = () => {
  showWxQrcode.value = false
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

// 关闭弹窗
const close = () => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
  showWxQrcode.value = false
  emit('update:visible', false)
}
</script>

<style scoped>
.get-power-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.dialog-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(6px);
}

.dialog-content {
  position: relative;
  max-width: 1000px;
  max-height: 85vh;
  width: 100%;
  background: rgba(15, 23, 42, 0.95);
  border: 1px solid rgba(94, 234, 212, 0.2);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(20px);
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(94, 234, 212, 0.1);
  border: 1px solid rgba(94, 234, 212, 0.2);
  color: #5eead4;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 10;
}

.close-button:hover {
  background: rgba(94, 234, 212, 0.2);
  transform: scale(1.05);
  border-color: rgba(94, 234, 212, 0.4);
}

.container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 30px 24px;
  max-height: 85vh;
  overflow-y: auto;
}

/* 自定义滚动条 */
.container::-webkit-scrollbar {
  width: 6px;
}

.container::-webkit-scrollbar-track {
  background: rgba(30, 41, 59, 0.3);
  border-radius: 3px;
}

.container::-webkit-scrollbar-thumb {
  background: rgba(94, 234, 212, 0.4);
  border-radius: 3px;
  transition: all 0.3s;
}

.container::-webkit-scrollbar-thumb:hover {
  background: rgba(94, 234, 212, 0.6);
}

/* Firefox 滚动条 */
.container {
  scrollbar-width: thin;
  scrollbar-color: rgba(94, 234, 212, 0.4) rgba(30, 41, 59, 0.3);
}

.header {
  text-align: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(94, 234, 212, 0.1);
}

.header h1 {
  color: #5eead4;
  font-size: 1.8rem;
  margin-bottom: 8px;
  font-weight: 700;
}

.header .subtitle {
  color: #94a3b8;
  font-size: 0.95rem;
  max-width: 700px;
  margin: 0 auto 16px;
  line-height: 1.5;
}

.current-balance {
  background: linear-gradient(135deg, rgba(94, 234, 212, 0.2), rgba(167, 139, 250, 0.2));
  color: white;
  border: 1px solid rgba(94, 234, 212, 0.3);
  border-radius: 12px;
  padding: 16px 20px;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 20px rgba(94, 234, 212, 0.1);
  backdrop-filter: blur(10px);
}

.balance-icon {
  font-size: 2rem;
  color: #5eead4;
}

.balance-amount {
  font-size: 1.8rem;
  font-weight: 700;
  color: #5eead4;
}

.balance-label {
  font-size: 0.85rem;
  opacity: 0.9;
  color: #cbd5e1;
}

.section {
  background: rgba(30, 41, 59, 0.4);
  border: 1px solid rgba(94, 234, 212, 0.15);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
}

.section-title {
  font-size: 1.2rem;
  color: #f1f5f9;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.section-title i {
  color: #5eead4;
}

.packages {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 16px;
  position: relative;
}



.package-card {
  border: 1px solid rgba(94, 234, 212, 0.2);
  background: rgba(30, 41, 59, 0.3);
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
}

.package-card:hover,
.package-card.active {
  border-color: #5eead4;
  box-shadow: 0 4px 20px rgba(94, 234, 212, 0.2);
  background: rgba(94, 234, 212, 0.1);
}

.package-card.active {
  background: rgba(94, 234, 212, 0.15);
}

.package-card .popular-badge {
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #f56c6c;
  color: white;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
}

.package-amount {
  font-size: 1.4rem;
  font-weight: 700;
  color: #5eead4;
  margin: 8px 0 4px;
}

.package-price {
  font-size: 1.2rem;
  font-weight: 600;
  color: #fbbf24;
  margin-bottom: 4px;
}

.package-desc {
  color: #94a3b8;
  font-size: 0.8rem;
  margin-top: 4px;
  line-height: 1.3;
}

.comparison {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.queue-type {
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.queue-type.free {
  background: rgba(30, 41, 59, 0.3);
  border: 1px solid rgba(148, 163, 184, 0.2);
}

.queue-type.vip {
  background: rgba(167, 139, 250, 0.1);
  border: 1px solid rgba(167, 139, 250, 0.25);
}

.queue-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.queue-title.free {
  color: #94a3b8;
}

.queue-title.vip {
  color: #a78bfa;
}

.feature-list {
  list-style: none;
  margin-top: 8px;
}

.feature-list li {
  padding: 6px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #cbd5e1;
}

.feature-list i {
  width: 16px;
  font-size: 0.8rem;
}

.feature-list i.fa-check {
  color: #5eead4;
}

.feature-list i.fa-times {
  color: #f87171;
}

.buy-button {
  background: linear-gradient(90deg, #5eead4, #a78bfa);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  width: 100%;
  margin-top: 12px;
}

.buy-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 24px rgba(94, 234, 212, 0.3);
}

.buy-button:active {
  transform: translateY(0);
}

.buy-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-loading {
  margin-right: 6px;
}

.package-empty {
  grid-column: 1 / -1;
  text-align: center;
  padding: 32px 16px;
  color: #64748b;
  font-size: 0.95rem;
}

.payment-methods {
  margin-top: 16px;
}

.payment-label {
  color: #94a3b8;
  font-size: 0.9rem;
  margin-bottom: 10px;
}

.payment-options {
  display: flex;
  gap: 12px;
}

.payment-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid rgba(94, 234, 212, 0.2);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  background: rgba(30, 41, 59, 0.3);
}

.payment-option.active {
  border-color: #5eead4;
  background: rgba(94, 234, 212, 0.1);
}

.payment-option input[type="radio"] {
  accent-color: #5eead4;
}

.payment-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.payment-option span {
  color: #e2e8f0;
  font-size: 0.9rem;
}

.free-notice {
  background: rgba(94, 234, 212, 0.08);
  border: 1px solid rgba(94, 234, 212, 0.15);
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.free-notice i {
  color: #5eead4;
  font-size: 1rem;
  margin-top: 2px;
}

.free-notice div {
  color: #cbd5e1;
  font-size: 0.85rem;
  line-height: 1.4;
}

.footer-note {
  text-align: center;
  color: #64748b;
  font-size: 0.8rem;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(94, 234, 212, 0.1);
  line-height: 1.4;
}

.footer-note i {
  color: #5eead4;
  margin-right: 4px;
}

.privacy-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: rgba(94, 234, 212, 0.06);
  border: 1px solid rgba(94, 234, 212, 0.15);
  border-radius: 8px;
  padding: 14px 18px;
  margin-top: 16px;
}

.privacy-notice i {
  color: #5eead4;
  font-size: 1.1rem;
  margin-top: 2px;
}

.privacy-notice strong {
  color: #5eead4;
  font-size: 0.9rem;
  display: block;
  margin-bottom: 4px;
}

.privacy-notice p {
  color: #94a3b8;
  font-size: 0.85rem;
  line-height: 1.5;
  margin: 0;
}

/* 使用建议区域 */
.section>div[style*="background-color: rgba(94, 234, 212, 0.1)"] {
  background: rgba(94, 234, 212, 0.08) !important;
  border: 1px solid rgba(94, 234, 212, 0.15) !important;
  border-radius: 8px !important;
  padding: 12px 16px !important;
  margin-top: 16px !important;
}

.section>div[style*="background-color: rgba(94, 234, 212, 0.1)"] h4 {
  color: #5eead4 !important;
  font-size: 0.9rem !important;
  margin-bottom: 6px !important;
  font-weight: 600 !important;
}

.section>div[style*="background-color: rgba(94, 234, 212, 0.1)"] p {
  color: #cbd5e1 !important;
  font-size: 0.85rem !important;
  line-height: 1.4 !important;
}

@media (max-width: 768px) {
  .packages {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .comparison {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .header h1 {
    font-size: 1.5rem;
  }

  .current-balance {
    flex-direction: column;
    text-align: center;
    gap: 8px;
    padding: 12px 16px;
  }

  .container {
    padding: 20px 16px;
  }

  .dialog-content {
    max-height: 90vh;
  }

  .get-power-dialog {
    padding: 10px;
  }
}

/* Mask overlay styles */
.package-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
}

.mask-content {
  text-align: center;
  color: #5eead4;
  padding: 20px;
}

.mask-content i {
  font-size: 2rem;
  margin-bottom: 12px;
  display: block;
}

.mask-content p {
  margin: 8px 0;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.4;
}

.contact-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
  padding: 4px 12px;
  background: rgba(94, 234, 212, 0.2);
  border: 1px solid rgba(94, 234, 212, 0.4);
  border-radius: 6px;
  color: #5eead4;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
}

.contact-btn:hover {
  background: rgba(94, 234, 212, 0.3);
  border-color: #5eead4;
  transform: translateY(-1px);
}

.contact-btn i {
  font-size: 0.75rem;
  margin: 0;
  display: inline;
}

/* 微信支付二维码弹窗样式 */
.qrcode-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qrcode-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
}

.qrcode-content {
  position: relative;
  background: rgba(15, 23, 42, 0.98);
  border: 1px solid rgba(94, 234, 212, 0.3);
  border-radius: 16px;
  padding: 30px;
  max-width: 360px;
  width: 90%;
  text-align: center;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.6);
}

.qrcode-header {
  margin-bottom: 20px;
}

.qrcode-logo {
  width: 48px;
  height: 48px;
  margin-bottom: 12px;
}

.qrcode-header h3 {
  color: #f1f5f9;
  font-size: 1.2rem;
  margin: 0;
}

.qrcode-body {
  margin-bottom: 20px;
}

.qrcode-image {
  width: 200px;
  height: 200px;
  border: 4px solid white;
  border-radius: 8px;
  margin-bottom: 16px;
}

.qrcode-tip {
  color: #94a3b8;
  font-size: 0.9rem;
  margin: 0 0 8px;
}

.qrcode-amount {
  color: #cbd5e1;
  font-size: 0.95rem;
  margin: 0;
}

.qrcode-amount span {
  color: #5eead4;
  font-weight: 700;
  font-size: 1.2rem;
}

.qrcode-footer {
  margin-top: 16px;
}

.qrcode-close-btn {
  background: rgba(148, 163, 184, 0.2);
  border: 1px solid rgba(148, 163, 184, 0.3);
  color: #94a3b8;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s;
}

.qrcode-close-btn:hover {
  background: rgba(148, 163, 184, 0.3);
  color: #f1f5f9;
}

@media (max-width: 768px) {
  .mask-content i {
    font-size: 1.5rem;
  }

  .mask-content p {
    font-size: 0.9rem;
  }
}
</style>